import { LBtn, LFlex, LModal, LSubTitle, LTitle } from "@components";
import { CheckerIcon } from "@fassessment/components/CheckerIcon";
import { MicCheckOpenModal } from "@fassessment/components/MicCheckOpenModal";
import { MicTroubleshootModal } from "@fassessment/components/MicTroubleShootModal";
import { playAudioAsync } from "@fassessment/hooks/useAudio";
import { useCustomSpeechToText } from "@fassessment/hooks/useCustomSpeechToText";
import { faCircleLeft } from "@fortawesome/free-regular-svg-icons";
import { faMicrophone, faVolumeHigh, faWifi } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sleepFor } from "@helpers/countHelper";
import { NavItems } from "@helpers/navigation";
import { useApi } from "@services/useApi";
import { useEffect, useState } from "react";
import { translate } from "react-i18nify";
import { NavLink, useNavigate } from "react-router-dom";
import { Env } from "../../../env";
import "../components/assessment-layout.scss";

enum CheckStatus {
    Pending,
    Success,
    Failed
}

enum Step {
    Wifi,
    Audio,
    Mic
}

const MicStatuses = (
    tryNumber: number,
    toggleMicCheckOpen: (tryTwo: boolean) => void,
    toggleGetHelp: () => void
): JSX.Element => {
    return (
        <LFlex justifyCenter alignCenter className="mt-6">
            <p className="mt-5 has-text-weight-bold has-text-dark-blue">
                {tryNumber === 2
                    ? translate("assessments.deviceCheck.microphone.troubleshootConnection")
                    : translate("assessments.deviceCheck.microphone.speakClearly")}
            </p>
            <LFlex row>
                {tryNumber === 2 && (
                    <LBtn classes="has-text-weight-bold mt-5" variation="outline" onClick={toggleGetHelp}>
                        {translate("assessments.deviceCheck.getHelp")}
                    </LBtn>
                )}
                <LBtn
                    onClick={() => {
                        toggleMicCheckOpen(true);
                    }}
                    classes="has-text-weight-bold mt-5"
                >
                    {translate("assessments.deviceCheck.tryAgain")}
                </LBtn>
            </LFlex>
        </LFlex>
    );
};

const ACTION_TIMEOUT = 2500;

interface TryAgain {
    text: string;
    onClick: () => void;
}

export const AssessmentChecks = (): JSX.Element => {
    const [micCheckOpen, setMicCheckOpen] = useState(false);
    const [micTroubleshootOpen, setMicTroubleshootOpen] = useState(false);
    const [audioCheckOpen, setAudioCheckOpen] = useState(false);
    const [minMax, setMinMax] = useState<[number, number]>([-100, -100]);
    const [checkStatus, setCheckStatus] = useState<CheckStatus>(CheckStatus.Pending);
    const [tryNumber, setTryNumber] = useState(1);
    const [timeoutNav, setTimeoutNav] = useState<NodeJS.Timeout>();
    const [currentStep, setCurrentStep] = useState(Step.Wifi);
    const [tryAgainText, setTryAgainText] = useState<TryAgain | undefined>();

    const navigate = useNavigate();

    const { connectionTest } = useApi();

    const toggleMicCheckOpen = (tryTwo?: boolean): void => {
        if (micCheckOpen) {
            void stopSpeechToText();
            const newStatus = minMax[1] > -50 ? CheckStatus.Success : CheckStatus.Failed;

            setCheckStatus(newStatus);
            setMicCheckOpen(!micCheckOpen);

            if (newStatus === CheckStatus.Success) {
                const navTimeout = setTimeout(() => {
                    destroy();
                    navigate(NavItems.Assessments.PracticeBeginRun.path);
                }, ACTION_TIMEOUT);

                setTimeoutNav(navTimeout);
            }

            return;
        }

        if (tryTwo) {
            setTryNumber(2);
        }
        setCheckStatus(CheckStatus.Pending);
        setMicCheckOpen(!micCheckOpen);
    };

    const toggleGetHelp = (): void => {
        setMicCheckOpen(false);
        setMicTroubleshootOpen(!micTroubleshootOpen);
    };

    const toggleAudioCheck = (): void => {
        setAudioCheckOpen(!audioCheckOpen);
    };

    const tryAgainGetHelp = (): void => {
        setMicTroubleshootOpen(false);
        setCheckStatus(CheckStatus.Pending);
        setTryNumber(1);
        setMicCheckOpen(true);
    };

    const { startSpeechToText, stopSpeechToText, recordingVolume, currentDecibels, destroy } = useCustomSpeechToText({
        testOnly: true
    });

    const handleAudioCheck = async (): Promise<void> => {
        setTryAgainText(undefined);
        const newAudio = new Audio(`${Env.REACT_APP_BLOB_URL}/instructions-en-us/bells.mp3`);
        await playAudioAsync(newAudio);
        setAudioCheckOpen(true);
    };

    const handleAudioError = (): void => {
        toggleAudioCheck();
        setTryAgainText({
            text: translate("assessments.deviceCheck.speakers.connectOrTurnUp"),
            onClick: handleAudioCheck
        });
    };

    const handleAudioSuccess = async (): Promise<void> => {
        setAudioCheckOpen(false);
        setCurrentStep(Step.Mic);
        await sleepFor(2);
        toggleMicCheckOpen();
    };

    const handleConnectionCheck = async (): Promise<void> => {
        setTryAgainText(undefined);
        const check = await connectionTest();

        if (!check) {
            setTryAgainText({
                text: translate("assessments.deviceCheck.internet.waitAndTryAgain"),
                onClick: handleConnectionCheck
            });
        } else {
            setCurrentStep(Step.Audio);
            await sleepFor(2);
            void handleAudioCheck();
        }
    };

    const handleSteps = async (): Promise<void> => {
        await handleConnectionCheck();
    };

    useEffect(() => {
        setTimeout(() => {
            void handleSteps();
        }, 2000);
    }, []);

    useEffect(() => {
        if (micCheckOpen && isFinite(currentDecibels)) {
            setMinMax([Math.min(currentDecibels, minMax[0]), Math.max(currentDecibels, minMax[1])]);
        }
    }, [currentDecibels]);

    return (
        <div className="assessment-checks-page">
            <LFlex alignCenter className="assessment-checks p-6 is-fullheight">
                <LFlex className="assessment-checks-header is-justify-content-space-between" row>
                    <div className="assessment-checks-back" style={{ position: "absolute", left: "5%" }}>
                        <NavLink
                            to="/admin/assessments/assessmentsUpcoming"
                            onClick={() => {
                                clearTimeout(timeoutNav);
                            }}
                        >
                            <FontAwesomeIcon icon={faCircleLeft} size={"xl"} />
                            <span className="subtitle px-2 has-text-primary-blue">
                                {translate("assessments.deviceCheck.back")}
                            </span>
                        </NavLink>
                    </div>
                    <LFlex alignCenter justifyCenter>
                        <LTitle color="has-text-dark-blue">{translate("assessments.deviceCheck.title")}</LTitle>
                        <LSubTitle size="5" color="has-text-dark-blue" className="mt-3">
                            {checkStatus === CheckStatus.Success
                                ? translate("assessments.deviceCheck.completed")
                                : translate("assessments.deviceCheck.intro")}
                        </LSubTitle>
                    </LFlex>
                </LFlex>
                <div
                    className="assessment-checks-list is-flex"
                    style={{ height: "80vh", width: "60vw", justifyContent: "space-between" }}
                >
                    <LFlex
                        alignCenter
                        className="assessment-checks-items is-justify-content-space-evenly"
                        row
                        fullWidth
                    >
                        {/* {CheckStatuses(CheckStatus.Pending, tryNumber, toggleMicCheckOpen, toggleGetHelp)} */}
                        <CheckerIcon
                            title={translate("assessments.deviceCheck.internet.title")}
                            subtitle={translate("assessments.deviceCheck.internet.checking")}
                            icon={faWifi}
                            active={currentStep === Step.Wifi}
                            success={currentStep !== Step.Wifi}
                            error={!!tryAgainText?.text && currentStep === Step.Wifi}
                            errorText={translate("assessments.deviceCheck.internet.error")}
                        />
                        <div className="assessment-checks-divider divider" style={{ width: "20%" }} />
                        <CheckerIcon
                            title={translate("assessments.deviceCheck.speakers.title")}
                            subtitle={translate("assessments.deviceCheck.speakers.checking")}
                            icon={faVolumeHigh}
                            active={currentStep === Step.Audio}
                            error={!!tryAgainText?.text && currentStep === Step.Audio}
                            errorText={translate("assessments.deviceCheck.speakers.error")}
                            success={currentStep === Step.Mic}
                        />
                        <div className="assessment-checks-divider divider" style={{ width: "20%" }} />
                        <CheckerIcon
                            title={translate("assessments.deviceCheck.microphone.title")}
                            subtitle={translate("assessments.deviceCheck.microphone.checking")}
                            icon={faMicrophone}
                            active={currentStep === Step.Mic}
                            error={checkStatus === CheckStatus.Failed}
                            success={checkStatus === CheckStatus.Success}
                            errorText={
                                tryNumber === 2
                                    ? translate("assessments.deviceCheck.microphone.notConnected")
                                    : translate("assessments.deviceCheck.microphone.pickingUpAudio")
                            }
                        />
                    </LFlex>
                </div>
                {tryAgainText?.text && (
                    <LFlex justifyCenter alignCenter className="mt-6">
                        <p className="mt-5 has-text-weight-bold has-text-dark-blue">{tryAgainText?.text}</p>
                        <LFlex row>
                            <LBtn onClick={tryAgainText.onClick} classes="has-text-weight-bold mt-5">
                                {translate("assessments.deviceCheck.tryAgain")}
                            </LBtn>
                        </LFlex>
                    </LFlex>
                )}
                {checkStatus === CheckStatus.Failed && MicStatuses(tryNumber, toggleMicCheckOpen, toggleGetHelp)}
            </LFlex>
            {micCheckOpen && (
                <MicCheckOpenModal
                    startSpeechToText={startSpeechToText}
                    recordingVolume={recordingVolume ?? 0}
                    onClose={toggleMicCheckOpen}
                />
            )}

            {micTroubleshootOpen && <MicTroubleshootModal onClose={toggleGetHelp} onTryAgain={tryAgainGetHelp} />}

            {audioCheckOpen && (
                <LModal
                    keepActionsVisibleOnMobileLandscape
                    title={
                        <p className="has-text-centered has-text-weight-bold">
                            {translate("assessments.deviceCheck.speakers.canHearBells")}
                        </p>
                    }
                    hasCancelConfirmButtons
                    onClose={handleAudioError}
                    onConfirm={handleAudioSuccess}
                    width="s"
                    fullHeight={false}
                    cancelText={translate("assessments.deviceCheck.no")}
                    confirmText={translate("assessments.deviceCheck.yes")}
                >
                    <div className="has-text-centered">
                        {translate("assessments.deviceCheck.speakers.increaseVolume")}
                    </div>
                </LModal>
            )}
        </div>
    );
};
